// The app's own right-click menu. Anything showing a person carries data-person-id and gets the
// person menu; charts add their own items for empty space; everything else gets a general menu.
// Text fields and selected text keep the browser's menu so copy and paste still work.

import { Crosshair, Eye, GitFork, Home, Merge, Pencil, Plus, Route, Search, Settings, Share2, Trash2, Undo2, Redo2, UserPlus, Copy, CircleHelp } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { displayName } from '../../model/people';
import { removePerson } from '../../model/tree';
import type { Id } from '../../model/types';
import { commit, confirmAction, focusPerson, getState, openDialog, redo, setState, setView, showPerson, toast, undo } from '../store';
import { Menu, type MenuEntry } from './Menu';

type State = { x: number; y: number; items: MenuEntry[] } | null;
let current: State = null;
const listeners = new Set<() => void>();
const set = (s: State) => {
  current = s;
  listeners.forEach((l) => l());
};

/** Where to put a menu for this event; keyboard-opened menus (no pointer position) go by the element. */
function pointOf(e: { clientX: number; clientY: number; target: EventTarget | null }) {
  if (e.clientX || e.clientY) return { x: e.clientX, y: e.clientY };
  const r = (e.target as Element)?.getBoundingClientRect?.();
  return r ? { x: r.left + 12, y: r.top + Math.min(r.height, 24) } : { x: 40, y: 40 };
}

export function openContextMenu(e: { clientX: number; clientY: number; target: EventTarget | null; preventDefault: () => void }, items: MenuEntry[]) {
  e.preventDefault();
  set({ ...pointOf(e), items });
}

export function ContextMenuHost() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
  if (!s) return null;
  return <Menu items={s.items} x={s.x} y={s.y} ariaLabel="Context menu" onClose={() => set(null)} />;
}

export function personMenuItems(id: Id): MenuEntry[] {
  const s = getState();
  const tree = s.tree;
  const p = tree?.people[id];
  if (!tree || !p) return [];
  const home = tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : undefined;
  const add = (kind: 'parent' | 'partner' | 'child' | 'sibling' | 'social', label: string) => ({ key: kind, label, onSelect: () => openDialog({ type: 'add-relative', personId: id, kind }) });
  return [
    { key: 'details', label: 'Show details', icon: <Eye size={14} />, onSelect: () => showPerson(id) },
    { key: 'edit', label: 'Edit…', icon: <Pencil size={14} />, hint: 'E', onSelect: () => openDialog({ type: 'edit-person', personId: id }) },
    {
      key: 'add',
      label: 'Add relative',
      icon: <Plus size={14} />,
      submenu: [add('parent', 'Parent…'), add('partner', 'Partner…'), add('child', 'Child…'), add('sibling', 'Sibling…'), add('social', 'Friend or other connection…')],
    },
    'separator',
    { key: 'tree', label: 'Center in tree', icon: <GitFork size={14} />, onSelect: () => focusPerson(id, { view: 'tree' }) },
    { key: 'network', label: 'Center in network', icon: <Share2 size={14} />, onSelect: () => focusPerson(id, { view: 'network' }) },
    {
      key: 'relate',
      label: home && home !== id ? `How are they related to ${displayName(tree.people[home])}?` : 'How are they related…',
      icon: <Route size={14} />,
      onSelect: () => {
        setState(home && home !== id ? { relateA: home, relateB: id } : { relateA: id, relateB: null });
        setView('relate');
      },
    },
    ...(home !== id
      ? [{ key: 'home', label: 'Make home person', icon: <Home size={14} />, onSelect: () => commit({ ...getState().tree!, homePersonId: id }) }]
      : []),
    { key: 'copy', label: 'Copy name', icon: <Copy size={14} />, onSelect: () => void navigator.clipboard?.writeText(displayName(p)).then(() => toast('Name copied.')) },
    'separator',
    { key: 'merge', label: 'Merge with…', icon: <Merge size={14} />, onSelect: () => openDialog({ type: 'merge', personId: id }) },
    {
      key: 'delete',
      label: 'Delete…',
      icon: <Trash2 size={14} />,
      danger: true,
      onSelect: () =>
        confirmAction({
          title: `Delete ${displayName(p)}?`,
          body: 'This removes the person and all of their relationships and group memberships. You can undo this right after.',
          confirmLabel: 'Delete person',
          danger: true,
          onConfirm: () => {
            commit(removePerson(getState().tree!, id));
            toast(`Deleted ${displayName(p)}.`, 'info', { label: 'Undo', run: undo });
          },
        }),
    },
  ];
}

/** Items for empty space anywhere in the app. */
export function appMenuItems(): MenuEntry[] {
  const s = getState();
  const home = s.tree?.homePersonId && s.tree.people[s.tree.homePersonId] ? s.tree.homePersonId : undefined;
  return [
    { key: 'add', label: 'Add a person…', icon: <UserPlus size={14} />, hint: 'N', onSelect: () => openDialog({ type: 'new-person' }) },
    { key: 'find', label: 'Find a person…', icon: <Search size={14} />, hint: '⌘K', onSelect: () => openDialog({ type: 'palette' }) },
    ...(home ? [{ key: 'home', label: 'Go to home person', icon: <Crosshair size={14} />, onSelect: () => focusPerson(home) }] : []),
    'separator',
    { key: 'undo', label: 'Undo', icon: <Undo2 size={14} />, disabled: !s.past.length, onSelect: undo },
    { key: 'redo', label: 'Redo', icon: <Redo2 size={14} />, disabled: !s.future.length, onSelect: redo },
    'separator',
    { key: 'settings', label: 'Settings', icon: <Settings size={14} />, onSelect: () => openDialog({ type: 'settings' }) },
    { key: 'help', label: 'Help and shortcuts', icon: <CircleHelp size={14} />, hint: '?', onSelect: () => openDialog({ type: 'help' }) },
  ];
}

/** Global fallback, attached once in App. Views that handle the event call preventDefault first. */
export function handleContextMenu(e: MouseEvent) {
  if (e.defaultPrevented) return;
  const target = e.target as Element;
  const sel = window.getSelection();
  // Keep the browser's menu for editing text and for copying a selection.
  if (target.closest?.('input, textarea, select, [contenteditable]') || (sel && !sel.isCollapsed && sel.toString().trim())) return;
  if (!getState().tree) return;
  const personEl = target.closest?.('[data-person-id]');
  const id = personEl?.getAttribute('data-person-id');
  if (id && getState().tree?.people[id]) return openContextMenu(e, personMenuItems(id));
  if (target.closest?.('[role=dialog]')) return e.preventDefault();
  openContextMenu(e, appMenuItems());
}
